import React, { useEffect, useState, RefObject } from 'react';

export interface TocItem {
  id: string;
  text: string;
  depth: number;
}

interface TableOfContentsProps {
  items: TocItem[];
  activeId?: string;
  scrollContainer?: RefObject<HTMLElement | null>;
}

export default function TableOfContents({ items, activeId, scrollContainer }: TableOfContentsProps) {
  const [currentActiveId, setCurrentActiveId] = useState<string>(activeId || '');

  useEffect(() => {
    if (activeId) {
      setCurrentActiveId(activeId);
    }
  }, [activeId]);

  // Scroll spy effect - uses the scroll container if provided
  useEffect(() => {
    const root = scrollContainer?.current || null;
    
    const observer = new IntersectionObserver(
      (entries) => {
        // Find the first intersecting entry
        const intersecting = entries.filter(e => e.isIntersecting);
        if (intersecting.length > 0) {
          // Use the first one that's intersecting
          setCurrentActiveId(intersecting[0].target.id);
        }
      },
      {
        root,
        rootMargin: '-80px 0px -80% 0px',
        threshold: 0,
      }
    );

    items.forEach((item) => {
      const element = document.getElementById(item.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [items, scrollContainer]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      // Use scrollIntoView which works correctly with any scroll container
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setCurrentActiveId(id);
    }
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <nav className="sticky top-20">
      <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wide">
        On this page
      </h3>
      <ul className="space-y-1">
        {items.map((item) => (
          <li
            key={item.id}
            style={{ paddingLeft: `${(item.depth - 2) * 12}px` }}
          >
            <a
              href={`#${item.id}`}
              onClick={(e) => handleClick(e, item.id)}
              className={`block py-1 text-sm transition-colors border-l-2 pl-3 -ml-px ${
                currentActiveId === item.id
                  ? 'text-primary border-primary font-medium'
                  : 'text-muted-foreground border-transparent hover:text-foreground hover:border-muted-foreground/50'
              }`}
            >
              {item.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
